import { useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getCurrentUserSelector, getCurrentAccountSelector } from 'selectors/index'
import {
  setVisibleFields,
  setAdminVisibleFields,
  saveDetailsFieldOrder,
  resetDetailsFieldsToDefault,
  saveAdminDetailsFieldOrder,
  resetAdminDetailsFieldsToDefault
} from 'Profiles/actions'
import { getVisibleFields, getAdminVisibleFields } from 'Profiles/selectors'
import { getAllowUsersToConfigureProfileDetailsFields } from 'selectors/Account'

export const fieldMap = {
  deals: 'deals_details_fields',
  companies: 'companies_details_fields',
  people: 'people_details_fields',
  customers: 'customers_details_fields'
}

export const singularProfileTypeMap = {
  deals: 'deal',
  companies: 'company',
  customers: 'company',
  people: 'person'
}

const DEFAULT_PRIMARY_FIELDS = {
  deals: ['deal_value', 'deal_stage', 'deal_time_to_close', 'deal_exp_close'],
  companies: ['company_total_pipeline', 'company_won_deals', 'company_children_total_pipeline', 'company_children_won_deals'],
  customers: ['company_total_pipeline', 'company_won_deals', 'company_children_total_pipeline', 'company_children_won_deals'],
  people: ['person_total_pipeline', 'person_won_deals', 'person_created_at', 'person_linked_deals']
}

const DEFAULT_SECONDARY_FIELDS = {
  deals: [
    'deal_time_to_dollar', 'deal_owner', 'deal_company_name', 'deal_pipeline', 'deal_probability', 'deal_status', 'deal_source',
    'deal_revenue_type', 'deal_product_lines', 'deal_collaborators'
  ],
  companies: ['company_parent', 'company_owner'],
  customers: ['customer_health_score', 'company_parent', 'company_owner', 'customer_pinned_milestones'],
  people: ['person_company_name', 'person_status', 'person_position', 'person_owner', 'person_source', 'person_type']
}

const DEFAULT_TERTIARY_FIELDS = {
  deals: ['deal_summary', 'deal_tags'],
  companies: ['company_summary', 'company_tags'],
  customers: ['company_summary', 'company_tags'],
  people: ['person_summary', 'person_tags']
}

const getContextActions = (settingsContext) => {
  if (settingsContext === 'admin') {
    return {
      setFields: setAdminVisibleFields,
      getFields: getAdminVisibleFields,
      save: saveAdminDetailsFieldOrder,
      reset: resetAdminDetailsFieldsToDefault
    }
  }

  return {
    setFields: setVisibleFields,
    getFields: getVisibleFields,
    save: saveDetailsFieldOrder,
    reset: resetDetailsFieldsToDefault
  }
}

export const useProfileDetailsFieldConfiguration = ({
  profileType, // deals, companies, customers, people
  customFieldLabels = [],
  settingsContext = 'user', // 'user' or 'admin'
  systemColumns,
  isConfiguring = false
}) => {
  const defaultPrimaryFields = DEFAULT_PRIMARY_FIELDS[profileType]
  const defaultSecondaryFields = DEFAULT_SECONDARY_FIELDS[profileType]
  const defaultTertiaryFields = DEFAULT_TERTIARY_FIELDS[profileType]

  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUserSelector)
  const currentAccount = useSelector(getCurrentAccountSelector)
  const allowUsersToConfigureProfileDetailsFields = useSelector(getAllowUsersToConfigureProfileDetailsFields)
  const canCustomize = currentUser.read_only ? false : allowUsersToConfigureProfileDetailsFields

  // Load Redux actions for user or admin context
  const actions = useMemo(() => getContextActions(settingsContext), [settingsContext])

  const userDetailsFields = currentUser[fieldMap[profileType]]
  const defaultAccountFields = currentAccount[fieldMap[profileType]]

  const getSavedFieldConfiguration = useCallback(() => {
    if (settingsContext === 'admin') {
      // For admin: load from account defaults only
      if (defaultAccountFields !== null && defaultAccountFields !== undefined) {
        return defaultAccountFields
      }
    } else {
      // For user: load user settings first, then account defaults
      if (canCustomize && userDetailsFields !== null && userDetailsFields !== undefined) {
        return userDetailsFields
      }
      if (defaultAccountFields !== null && defaultAccountFields !== undefined) {
        return defaultAccountFields
      }
    }
    // If no user or admin fields are available, use defaults
    return [...defaultPrimaryFields, ...defaultSecondaryFields, ...defaultTertiaryFields]
  }, [userDetailsFields, defaultAccountFields, defaultPrimaryFields, defaultSecondaryFields, defaultTertiaryFields, settingsContext])

  // Only initialize Redux when entering configuration mode
  useEffect(() => {
    if (isConfiguring) {
      dispatch(actions.setFields({
        profileType,
        fields: getSavedFieldConfiguration()
      }))
    }
  }, [isConfiguring, dispatch, profileType, getSavedFieldConfiguration, actions.setFields])

  // Read draft configuration from Redux (only populated when isConfiguring)
  const draftFieldConfiguration = useSelector(state => actions.getFields(state, profileType))

  // Use draft if configuring, otherwise use saved preferences directly
  const activeFields = useMemo(() => {
    if (isConfiguring && draftFieldConfiguration) {
      return draftFieldConfiguration
    }
    return getSavedFieldConfiguration()
  }, [isConfiguring, draftFieldConfiguration, getSavedFieldConfiguration])

  const visiblePrimaryFields = useMemo(() => activeFields.filter(key => defaultPrimaryFields.includes(key)), [activeFields, defaultPrimaryFields])
  const visibleSecondaryFields = useMemo(() => activeFields.filter(key => defaultSecondaryFields.includes(key) || key.startsWith('custom_field_')), [activeFields, defaultSecondaryFields])
  const visibleTertiaryFields = useMemo(() => activeFields.filter(key => defaultTertiaryFields.includes(key)), [activeFields, defaultTertiaryFields])
  const hiddenPrimaryFields = useMemo(() => defaultPrimaryFields.filter(key => !visiblePrimaryFields.includes(key)), [defaultPrimaryFields, visiblePrimaryFields])
  const hiddenSecondaryFields = useMemo(() => defaultSecondaryFields.filter(key => !visibleSecondaryFields.includes(key)), [defaultSecondaryFields, visibleSecondaryFields])
  const hiddenTertiaryFields = useMemo(() => defaultTertiaryFields.filter(key => !visibleTertiaryFields.includes(key)), [defaultTertiaryFields, visibleTertiaryFields])

  const movePrimaryField = useCallback((dragIndex, hoverIndex) => {
    const allPrimaryFieldsForConfig = [...visiblePrimaryFields, ...hiddenPrimaryFields]
    const newAllPrimary = [...allPrimaryFieldsForConfig]
    const draggedItem = newAllPrimary[dragIndex]
    newAllPrimary.splice(dragIndex, 1)
    newAllPrimary.splice(hoverIndex, 0, draggedItem)

    const newVisiblePrimary = newAllPrimary.filter(key => visiblePrimaryFields.includes(key))
    const newVisibleFields = [...newVisiblePrimary, ...visibleSecondaryFields, ...visibleTertiaryFields]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, visibleSecondaryFields, visibleTertiaryFields, hiddenPrimaryFields, actions.setFields])

  const moveSecondaryField = useCallback((dragIndex, hoverIndex) => {
    const newVisibleSecondary = [...visibleSecondaryFields]
    const draggedItem = newVisibleSecondary[dragIndex]
    newVisibleSecondary.splice(dragIndex, 1)
    newVisibleSecondary.splice(hoverIndex, 0, draggedItem)

    const newVisibleFields = [...visiblePrimaryFields, ...newVisibleSecondary, ...visibleTertiaryFields]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, visibleSecondaryFields, visibleTertiaryFields, actions.setFields])

  const togglePrimaryFieldVisibility = useCallback((fieldKey) => {
    const isCurrentlyVisible = visiblePrimaryFields.includes(fieldKey)

    let newVisiblePrimary
    if (isCurrentlyVisible) {
      newVisiblePrimary = visiblePrimaryFields.filter(key => key !== fieldKey)
    } else {
      newVisiblePrimary = [...visiblePrimaryFields, fieldKey]
    }

    const newVisibleFields = [...newVisiblePrimary, ...visibleSecondaryFields, ...visibleTertiaryFields]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, visibleSecondaryFields, visibleTertiaryFields, actions.setFields])

  const toggleSecondaryFieldVisibility = useCallback((fieldKeys) => {
    const keysArray = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys]

    let newVisibleSecondary = [...visibleSecondaryFields]

    keysArray.forEach(fieldKey => {
      const isCurrentlyVisible = newVisibleSecondary.includes(fieldKey)

      if (isCurrentlyVisible) {
        newVisibleSecondary = newVisibleSecondary.filter(key => key !== fieldKey)
      } else {
        newVisibleSecondary.push(fieldKey)
      }
    })

    const newVisibleFields = [...visiblePrimaryFields, ...newVisibleSecondary, ...visibleTertiaryFields]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, visibleSecondaryFields, visibleTertiaryFields, actions.setFields])

  const selectAllFields = useCallback((selected) => {
    let newVisibleSecondary
    if (selected) {
      const allCustomFieldKeys = customFieldLabels.map(cf => `custom_field_${cf.id}`) || []
      newVisibleSecondary = [...defaultSecondaryFields, ...allCustomFieldKeys]
    } else {
      newVisibleSecondary = []
    }
    const newVisibleFields = [...visiblePrimaryFields, ...newVisibleSecondary, ...visibleTertiaryFields]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, defaultSecondaryFields, visibleTertiaryFields, customFieldLabels, actions.setFields])

  const toggleTertiaryFieldVisibility = useCallback((fieldKey) => {
    const isCurrentlyVisible = visibleTertiaryFields.includes(fieldKey)

    let newVisibleTertiary
    if (isCurrentlyVisible) {
      newVisibleTertiary = visibleTertiaryFields.filter(key => key !== fieldKey)
    } else {
      newVisibleTertiary = [...visibleTertiaryFields, fieldKey]
    }

    const newVisibleFields = [...visiblePrimaryFields, ...visibleSecondaryFields, ...newVisibleTertiary]
    dispatch(actions.setFields({ profileType, fields: newVisibleFields }))
  }, [dispatch, profileType, visiblePrimaryFields, visibleSecondaryFields, visibleTertiaryFields, actions.setFields])

  const resetFieldsToDefault = useCallback(async () => {
    await dispatch(actions.reset({ profileType }))
  }, [dispatch, profileType, actions.reset])

  const saveFieldOrder = useCallback(async () => {
    await dispatch(actions.save({ profileType }))
  }, [dispatch, profileType, actions.save])

  const resetFieldsToLastSavedOrder = useCallback(() => {
    dispatch(actions.setFields({
      profileType,
      fields: getSavedFieldConfiguration()
    }))
  }, [dispatch, profileType, getSavedFieldConfiguration, actions.setFields])

  const getFieldTitle = useCallback((fieldKey) => {
    if (fieldKey.startsWith('custom_field_')) {
      const customFieldId = parseInt(fieldKey.replace('custom_field_', ''))
      const customFieldLabel = customFieldLabels?.find(cf => cf.id === customFieldId)
      return customFieldLabel?.name || 'Unknown Field'
    }

    const column = systemColumns.find(col => col.id === fieldKey)
    return column?.name || fieldKey
  }, [customFieldLabels, systemColumns])

  const hasUnsavedChanges = useMemo(() => {
    if (!isConfiguring) return false

    const savedFields = getSavedFieldConfiguration()
    return JSON.stringify(savedFields) !== JSON.stringify(activeFields)
  }, [isConfiguring, getSavedFieldConfiguration, activeFields])

  const managerColumns = useMemo(() => {
    const systemFields = systemColumns.filter(col =>
      defaultSecondaryFields.includes(col.id)
    )

    const customFields = (customFieldLabels || []).map(cf => ({
      id: `custom_field_${cf.id}`,
      name: cf.name,
      group: singularProfileTypeMap[profileType],
      subgroup: cf.custom_field_group_id
        ? `custom_group_${cf.custom_field_group_id}`
        : 'other'
    }))

    return [...systemFields, ...customFields]
  }, [systemColumns, defaultSecondaryFields, customFieldLabels])

  return {
    visiblePrimaryFields,
    visibleSecondaryFields,
    visibleTertiaryFields,
    hiddenPrimaryFields,
    hiddenSecondaryFields,
    hiddenTertiaryFields,
    movePrimaryField,
    moveSecondaryField,
    togglePrimaryFieldVisibility,
    toggleSecondaryFieldVisibility,
    toggleTertiaryFieldVisibility,
    selectAllFields,
    resetFieldsToDefault,
    resetFieldsToLastSavedOrder,
    saveFieldOrder,
    getFieldTitle,
    hasUnsavedChanges,
    defaultPrimaryFields,
    defaultSecondaryFields,
    defaultTertiaryFields,
    managerColumns
  }
}
